import { captureException } from '@sentry/nextjs';
import { AUTHENTICATION_MODAL_WALLETS, LS_AUTH_DATA_KEY } from 'constants/user';
import { RPCError, RPCErrorCode } from 'magic-sdk';
import { Dispatch } from 'react';
import { getMagicLinkClient } from 'services/magicLinkClient';
import {
  addEmail,
  addNewAddressToUser,
  authenticateLedger,
  deleteFileFilgram,
  getAllowance,
  getUser,
  getUserAllowances,
  getWalletTypeFromUserData,
  loginViaEmail,
  requestAllowance,
  RequestAllowanceParams,
  updateFileFilgram,
  updateUserData,
  uploadFileFilgram,
  uploadFileWeb3Storage,
  verifySignDataAddress,
} from 'services/userService';
import {
  getLocalStorageData,
  removeLocalStorageData,
  setLocalStorageData,
} from 'services/utilsService';
import {
  ADD_EMAIL_FAILURE,
  ADD_EMAIL_REQUEST,
  ADD_EMAIL_SUCCESS,
  AUTHENTICATE_ADDRESS_FAILURE,
  AUTHENTICATE_ADDRESS_REQUEST,
  AUTHENTICATE_ADDRESS_SUCCESS,
  AUTHENTICATE_EMAIL_FAILURE,
  AUTHENTICATE_EMAIL_REQUEST,
  AUTHENTICATE_EMAIL_SUCCESS,
  AUTHENTICATE_LEDGER_FAILURE,
  AUTHENTICATE_LEDGER_REQUEST,
  AUTHENTICATE_LEDGER_SUCCESS,
  CLEAR_AUTHENTICATE,
  GET_ALLOWANCES_FAILURE,
  GET_ALLOWANCES_REQUEST,
  GET_ALLOWANCES_SUCCESS,
  GET_ALLOWANCE_REQUEST,
  GET_ALLOWANCE_SUCCESS,
  GET_USER_FAILURE,
  GET_USER_REQUEST,
  GET_USER_SUCCESS,
  REQUEST_ALLOWANCE_FAILURE,
  REQUEST_ALLOWANCE_REQUEST,
  REQUEST_ALLOWANCE_SUCCESS,
  UPDATE_USER_REQUEST,
  UPDATE_USER_SUCCESS,
  WALLET_CHECK_SUPPORTED_FAILURE,
  WALLET_CHECK_SUPPORTED_REQUEST,
  WALLET_CHECK_SUPPORTED_SUCCESS,
  CLEAR_UPDATE_USER,
  CLEAR_AUTHENTICATE_STATUS,
  SET_IS_STORAGE_PROVIDER,
} from 'state/actionTypes/userActionTypes';
import {
  UserAction,
  UserContextState,
} from 'state/contexts/userContext/userContext.types';
import { Allowance, FilgramFilePayload, SignData, UserWalletType } from 'types/user';
import { UPDATE_USER_FAILURE } from '../actionTypes/userActionTypes';

type GetUserAction = (dispatch: Dispatch<UserAction>) => Promise<void>;
export const getUserAction: GetUserAction = async (dispatch) => {
  dispatch({ type: GET_USER_REQUEST });

  try {
    const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
    if (!localStorageAuthData) throw new Error('Missing auth token');

    const { token, walletAddress } = localStorageAuthData;
    const user = await getUser(token);

    const walletType = getWalletTypeFromUserData(user, walletAddress) as UserWalletType;
    dispatch({ type: GET_USER_SUCCESS, payload: { user, walletType, walletAddress } });
  } catch (err) {
    dispatch({ type: GET_USER_FAILURE, payload: (err as Error).message });

    if ((err as Error).message !== 'Load failed')
      removeLocalStorageData(LS_AUTH_DATA_KEY);
  }
};

type GetAllowancesAction = (dispatch: Dispatch<UserAction>) => Promise<void>;
export const getAllowancesAction: GetAllowancesAction = async (dispatch) => {
  dispatch({ type: GET_ALLOWANCES_REQUEST });

  try {
    const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
    if (!localStorageAuthData) throw new Error('Missing auth token');

    const payload = await getUserAllowances(localStorageAuthData.token);

    dispatch({ type: GET_ALLOWANCES_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: GET_ALLOWANCES_FAILURE, payload: (err as Error).message });
  }
};

type RequestAllowanceAction = (
  dispatch: Dispatch<UserAction>,
  allowanceRequestParams: RequestAllowanceParams,
) => Promise<void>;
export const requestAllowanceAction: RequestAllowanceAction = async (
  dispatch,
  allowanceRequestParams,
) => {
  dispatch({ type: REQUEST_ALLOWANCE_REQUEST });

  try {
    const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
    if (!localStorageAuthData) throw new Error('Missing auth token');

    const payload = await requestAllowance(
      localStorageAuthData.token,
      allowanceRequestParams,
    );

    dispatch({ type: REQUEST_ALLOWANCE_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: REQUEST_ALLOWANCE_FAILURE, payload: (err as Error).message });
  }
};

type GetAllowanceAction = (
  dispatch: Dispatch<UserAction>,
  allowanceId: string,
  allowance?: Allowance,
) => Promise<void>;
export const getAllowanceAction: GetAllowanceAction = async (
  dispatch,
  allowanceId,
  allowance,
) => {
  dispatch({ type: GET_ALLOWANCE_REQUEST });

  try {
    const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
    if (!localStorageAuthData) throw new Error('Missing auth token');

    const payload = await getAllowance(
      localStorageAuthData.token,
      allowanceId,
      allowance,
    );

    dispatch({ type: GET_ALLOWANCE_SUCCESS, payload });
  } catch (err) {
    dispatch({ type: GET_ALLOWANCES_FAILURE, payload: (err as Error).message });
  }
};

type AuthenticateLedgerAction = (dispatch: Dispatch<UserAction>) => Promise<SignData>;
export const authenticateLedgerAction: AuthenticateLedgerAction = async (dispatch) => {
  dispatch({ type: AUTHENTICATE_LEDGER_REQUEST });

  try {
    const payload = await authenticateLedger();

    dispatch({ type: AUTHENTICATE_LEDGER_SUCCESS });

    return payload;
  } catch (err) {
    dispatch({ type: AUTHENTICATE_LEDGER_FAILURE, payload: (err as Error).message });

    throw err;
  }
};

type AuthenticateAddressAction = (
  signData: SignData,
  walletType: UserWalletType,
  dispatch: Dispatch<UserAction>,
  shouldAddAddress: boolean,
) => Promise<void>;
export const authenticateAddressAction: AuthenticateAddressAction = async (
  signData,
  walletType,
  dispatch,
  shouldAddAddress,
) => {
  dispatch({ type: AUTHENTICATE_ADDRESS_REQUEST, walletType, signData });
  let fullSignData = { ...signData };

  try {
    if (walletType === UserWalletType.Ledger) {
      fullSignData = await authenticateLedgerAction(dispatch);
    }

    const { serverSignatureType } = AUTHENTICATION_MODAL_WALLETS[walletType];
    if (shouldAddAddress) {
      await addNewAddressToUser(serverSignatureType, fullSignData);
    } else {
      const { token, address } = await verifySignDataAddress(
        serverSignatureType,
        fullSignData,
      );
      setLocalStorageData(LS_AUTH_DATA_KEY, { token, walletAddress: address });
    }

    await getUserAction(dispatch);

    dispatch({
      type: AUTHENTICATE_ADDRESS_SUCCESS,
      walletType,
      payload: fullSignData.address,
    });
  } catch (err) {
    dispatch({
      type: AUTHENTICATE_ADDRESS_FAILURE,
      walletType,
      payload: (err as Error).message,
    });
  }
};

type loginViaEmailAction = (
  didToken: string,
  dispatch: Dispatch<UserAction>,
) => Promise<void>;
export const loginViaEmailAction: loginViaEmailAction = async (didToken, dispatch) => {
  dispatch({ type: AUTHENTICATE_EMAIL_REQUEST });

  try {
    const token = await loginViaEmail(didToken);
    setLocalStorageData(LS_AUTH_DATA_KEY, { token, walletAddress: '' });

    await getUserAction(dispatch);

    dispatch({
      type: AUTHENTICATE_EMAIL_SUCCESS,
    });
  } catch (err) {
    dispatch({
      type: AUTHENTICATE_EMAIL_FAILURE,
      payload: {
        message:
          err instanceof RPCError ? RPCErrorCode[err.code] : (err as Error).message,
      },
    });
  }
};

type AuthenticateEmailAction = (
  loginParams: { email: string; redirectURI?: string },
  dispatch: Dispatch<UserAction>,
) => Promise<void>;
export const authenticateEmailAction: AuthenticateEmailAction = async (
  loginParams,
  dispatch,
) => {
  dispatch({ type: AUTHENTICATE_EMAIL_REQUEST });

  try {
    const didToken = await getMagicLinkClient().auth.loginWithMagicLink({
      ...loginParams,
      showUI: false,
    });

    if (!didToken) {
      throw new Error('Magic link login failed');
    }

    const token = await loginViaEmail(didToken);
    setLocalStorageData(LS_AUTH_DATA_KEY, { token, walletAddress: '' });

    await getUserAction(dispatch);

    dispatch({
      type: AUTHENTICATE_EMAIL_SUCCESS,
    });
  } catch (err) {
    dispatch({
      type: AUTHENTICATE_EMAIL_FAILURE,
      payload: { message: (err as Error).message },
    });
  }
};

type AddEmailAction = (email: string, dispatch: Dispatch<UserAction>) => Promise<void>;
export const addEmailAction: AddEmailAction = async (email, dispatch) => {
  dispatch({ type: ADD_EMAIL_REQUEST });

  try {
    const client = await getMagicLinkClient();
    const didToken = await client.auth.loginWithMagicLink({
      email,
      showUI: false,
    });

    if (!didToken) {
      throw new Error('Magic link login failed');
    }

    const user = await addEmail(didToken);

    dispatch({
      type: ADD_EMAIL_SUCCESS,
      payload: {
        user,
      },
    });
  } catch (err) {
    dispatch({
      type: ADD_EMAIL_FAILURE,
      payload: { message: (err as Error).message },
    });
  }
};

type ClearAuthenticateAction = (dispatch: Dispatch<UserAction>) => void;
export const clearAuthenticateAction: ClearAuthenticateAction = (dispatch) => {
  dispatch({ type: CLEAR_AUTHENTICATE });
};

type WalletCheckSupportedAction = (
  isWalletSupported: () => Promise<boolean>,
  walletType: UserWalletType,
  dispatch: Dispatch<UserAction>,
) => Promise<void>;
export const walletCheckSupportedAction: WalletCheckSupportedAction = async (
  isWalletSupported,
  walletType,
  dispatch,
) => {
  dispatch({ type: WALLET_CHECK_SUPPORTED_REQUEST, walletType });

  try {
    const payload = await isWalletSupported();

    dispatch({ type: WALLET_CHECK_SUPPORTED_SUCCESS, walletType, payload });
  } catch (err) {
    dispatch({
      type: WALLET_CHECK_SUPPORTED_FAILURE,
      walletType,
      payload: (err as Error).message,
    });
  }
};

type UploadFilesAction = (uploadFilesData: {
  file: File;
  filgramFile: FilgramFilePayload;
  onStoredChunk: (chunkSize: number) => void;
  setFileToDoneIfAlreadyUploaded: (cid: string) => void;
  handleErrors: (key: string, error: string) => void;
}) => Promise<void>;
export const UploadFilesAction: UploadFilesAction = async ({
  file,
  filgramFile,
  onStoredChunk,
  setFileToDoneIfAlreadyUploaded,
  handleErrors,
}) => {
  try {
    const data = await uploadFileFilgram(filgramFile);
    if (data.status === 'PINNED_TO_IPFS' || data.status === 'STORED_ON_FILECOIN') {
      return setFileToDoneIfAlreadyUploaded(data.cid);
    }
    if (data.status === 'FOR_UPLOAD') {
      try {
        const web3StorageCid = await uploadFileWeb3Storage(file, onStoredChunk);
        if (web3StorageCid !== filgramFile.cid) {
          captureException({
            message: `CID mismatch. Local CID: ${filgramFile.cid}. Web3 Storage CID: ${web3StorageCid}`,
          });
          // Web3 Storage Client delete is not implemented yet
          // await deleteFileWeb3Storage(web3StorageCid);

          throw new Error('CID mismatch');
        }
      } catch (err) {
        await deleteFileFilgram(filgramFile.cid);
        throw err;
      }
      await updateFileFilgram({ cid: filgramFile.cid, status: 'PINNED_TO_IPFS' });
    }
  } catch (err) {
    handleErrors(filgramFile.cid, (err as Error).message);
  }
};

type DeleteFileFilgramAction = (uploadFilesData: { cid: string }) => Promise<void>;
export const deleteFileFilgramAction: DeleteFileFilgramAction = async ({ cid }) => {
  try {
    await deleteFileFilgram(cid);
  } catch (err) {
    console.log('err', (err as Error).message);
  }
};

type UpdateUserDataAction = (
  data: {
    email?: string;
    name?: string;
    company?: string;
    avatarCode?: string;
  },
  dispatch: Dispatch<UserAction>,
) => Promise<void>;
export const updateUserDataAction: UpdateUserDataAction = async (data, dispatch) => {
  try {
    dispatch({
      type: UPDATE_USER_REQUEST,
    });
    const payload = await updateUserData(data);
    dispatch({
      type: UPDATE_USER_SUCCESS,
      payload,
    });
  } catch (err) {
    dispatch({
      type: UPDATE_USER_FAILURE,
      payload: (err as Error).message,
    });
  }
};

type ClearUpdatingUserAction = (
  dispatch: Dispatch<UserAction>,
  getState: () => UserContextState,
) => void;
export const clearUpdatingUserAction: ClearUpdatingUserAction = (dispatch, getState) => {
  if (getState().updateUserRequest.status === 'Success') {
    dispatch({ type: CLEAR_UPDATE_USER });
  }
};

type UpdateEmail = (email: string, dispatch: Dispatch<UserAction>) => Promise<void>;
export const updateEmail: UpdateEmail = async (email, dispatch) => {
  const client = getMagicLinkClient();

  try {
    dispatch({
      type: AUTHENTICATE_EMAIL_REQUEST,
    });
    const isUpdated = await client.user.updateEmail({ email, showUI: false });
    if (isUpdated) {
      removeLocalStorageData(LS_AUTH_DATA_KEY);
      const token = await client.user.getIdToken();
      loginViaEmailAction(token, dispatch);
    }
  } catch (err) {
    dispatch({
      type: AUTHENTICATE_EMAIL_FAILURE,
      payload: {
        message:
          err instanceof RPCError ? RPCErrorCode[err.code] : (err as Error).message,
      },
    });
  }
};

type ClearAuthenticateStatusAction = (dispatch: Dispatch<UserAction>) => void;
export const clearAuthenticateStatusAction: ClearAuthenticateStatusAction = (
  dispatch,
) => {
  dispatch({ type: CLEAR_AUTHENTICATE_STATUS });
};

type SetIsStorageProvider = (dispatch: Dispatch<UserAction>, payload: boolean) => void;
export const setIsStorageProvider: SetIsStorageProvider = (dispatch, payload) => {
  dispatch({ type: SET_IS_STORAGE_PROVIDER, payload });
};
